import type { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import {
  InformationCircleOutlineIc,
  AlertCircleOutlineIc,
  CheckmarkCircleOutlineIc,
  CloseOutlineIc,
} from '@dsch/dd-icons';

import { ThemeTypes } from 'types/Theme.namespace';
import { space, ISpace } from 'helpers/genericStyles';
import { media } from 'helpers/breakpoints';

type Variants = 'INLINE' | 'TOAST';
type GapVariants = 'SMALL' | 'DEFAULT';
type Types = 'INFO' | 'SUCCESS' | 'ERROR' | 'PLAIN';
type Sizes = 'DEFAULT' | 'LARGE' | 'SMALL';
type ColorSet = {
  [key: string]: ThemeTypes.Colors;
};

export interface NotificationProps extends ISpace {
  variant?: Variants;
  gapVariant?: GapVariants;
  type: Types;
  icon?: string;
  hideIcon?: boolean;
  className?: string;
  size?: Sizes;
  /*  
    This prop accepts plain text or a string that also contains links such as 
    Testing a string with <a href="#">Link A</a> and then <a href="#">Link B</a>
  */
  title?: string;
  text: string | ReactNode;
  iconColor?: ThemeTypes.Colors;
  textColor?: ThemeTypes.Colors;
  includeCloseButton?: boolean;
  handleCallback?: () => void;
  largeIconOnDesktop?: boolean;
}

const containerColorSet: ColorSet = {
  INFO: 'BLUE_LIGHTER',
  SUCCESS: 'GREEN_LIGHTER',
  ERROR: 'RED_LIGHTER',
  PLAIN: 'GREY_LIGHTER',
};

const iconColorSet: ColorSet = {
  INFO: 'BLUE',
  SUCCESS: 'GREEN',
  ERROR: 'RED',
  PLAIN: 'GREY',
};

type TContainer = {
  variant: Variants;
  type: Types;
  size: Sizes;
  includeCloseButton: boolean;
  hideIcon?: boolean;
  largeIconOnDesktop?: boolean;
};
const Container = styled.div<TContainer>`
  ${space};
  ${({ theme, size }) =>
    size === 'DEFAULT' || size === 'SMALL'
      ? theme.fontSize.B12
      : theme.fontSize.M16};
  padding: ${({ theme, size, variant }) =>
    size === 'DEFAULT'
      ? variant === 'TOAST'
        ? `${theme.spacing.S8}`
        : `${theme.spacing.S8} ${theme.spacing.M16}`
      : theme.spacing.M16};

  line-height: ${({ theme }) => theme.spacing.M16};
  max-width: ${({ variant }) => (variant === 'TOAST' ? '320px' : '100%')};
  background: ${({ theme, type }) => theme.colors[containerColorSet[type]]};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  display: flex;
  align-items: center;
  position: relative;

  ${media.large} {
    ${({ variant, largeIconOnDesktop, theme }) =>
      variant === 'TOAST' &&
      largeIconOnDesktop &&
      `
      ${theme.fontSize.M16}; 
      padding: ${theme.spacing.M16}
      `}
  }
`;

type TIconContainer = {
  type: Types;
  gapVariant: GapVariants;
  iconColor?: ThemeTypes.Colors;
};
const IconContainer = styled.div<TIconContainer>`
  margin-right: ${({ theme, gapVariant }) =>
    gapVariant === 'SMALL' ? theme.spacing.S8 : theme.spacing.M16};
  display: inline-block;
  color: ${({ theme, type, iconColor }) =>
    iconColor ? theme.colors[iconColor] : theme.colors[iconColorSet[type]]};
  line-height: ${({ theme }) => theme.spacing.M24};
`;

type TTitle = { size: Sizes };
const Title = styled.p<TTitle>`
  font-weight: bold;
  flex: 1;

  ${({ theme, size }) => size === 'LARGE' && theme.fontSize.M16};
`;

type TText = {
  variant: Variants;
  textColor?: ThemeTypes.Colors;
  size: Sizes;
  hideIcon?: boolean;
};
export const Text = styled.p<TText>`
  color: ${({ theme, variant, textColor }) =>
    textColor
      ? theme.colors[textColor]
      : variant === 'TOAST'
      ? theme.colors.GREY_DARKER
      : theme.colors.GREY_DARK};
  display: inline-block;
  ${({ theme, size }) => size === 'LARGE' && theme.fontSize.B12};
  ${({ hideIcon, theme }) => hideIcon && `padding: ${theme.spacing.S4} 0`};
`;

const CloseButton = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.S4};
  right: ${({ theme }) => theme.spacing.S4};
  color: ${({ theme }) => theme.colors.GREY};
  cursor: pointer;
`;

const iconCSS = css`
  ${media.large} {
    width: 32px;
    height: 32px;
  }
`;

const SCheckmarkCircleOutlineIc = styled(CheckmarkCircleOutlineIc)`
  ${iconCSS}
`;

const SAlertCircleOutlineIc = styled(AlertCircleOutlineIc)`
  ${iconCSS}
`;

const SInformationCircleOutlineIc = styled(InformationCircleOutlineIc)`
  ${iconCSS}
`;

function assignSize(size: Sizes) {
  switch (size) {
    case 'LARGE':
      return '40';
    case 'SMALL':
      return '16';
    default:
      return '24';
  }
}

function iconSelector(
  iconType: string,
  size: Sizes,
  icon?: string,
  largeIconOnDesktop?: boolean,
) {
  const sizeCheck = assignSize(size);

  if (icon) {
    return <img src={icon} alt="" width={sizeCheck} height={sizeCheck} />;
  }
  switch (iconType) {
    case 'SUCCESS':
      return largeIconOnDesktop ? (
        <SCheckmarkCircleOutlineIc width={sizeCheck} height={sizeCheck} />
      ) : (
        <CheckmarkCircleOutlineIc width={sizeCheck} height={sizeCheck} />
      );
    case 'ERROR':
      return largeIconOnDesktop ? (
        <SAlertCircleOutlineIc width={sizeCheck} height={sizeCheck} />
      ) : (
        <AlertCircleOutlineIc width={sizeCheck} height={sizeCheck} />
      );
    default:
      return largeIconOnDesktop ? (
        <SInformationCircleOutlineIc width={sizeCheck} height={sizeCheck} />
      ) : (
        <InformationCircleOutlineIc width={sizeCheck} height={sizeCheck} />
      );
  }
}

function Notification({
  variant = 'INLINE',
  size = 'DEFAULT',
  gapVariant = 'DEFAULT',
  type,
  icon,
  hideIcon = false,
  text,
  iconColor,
  textColor,
  title,
  className,
  includeCloseButton = false,
  handleCallback,
  largeIconOnDesktop,
  ...spaceProps
}: NotificationProps) {
  return (
    <Container
      variant={variant}
      type={type}
      size={size}
      className={className}
      data-testid="notification"
      includeCloseButton={includeCloseButton}
      largeIconOnDesktop={largeIconOnDesktop}
      {...spaceProps}
    >
      {hideIcon || (
        <IconContainer
          type={type}
          gapVariant={gapVariant}
          iconColor={iconColor}
        >
          {iconSelector(type, size, icon, largeIconOnDesktop)}
        </IconContainer>
      )}
      <div>
        {title && <Title size={size}>{title}</Title>}
        <Text
          variant={variant}
          textColor={textColor}
          size={size}
          hideIcon={hideIcon}
        >
          {text}
        </Text>
      </div>
      {includeCloseButton && (
        <CloseButton
          data-testid="close-button"
          onClick={() => handleCallback && handleCallback()}
        >
          <CloseOutlineIc width="12" height="12" />
        </CloseButton>
      )}
    </Container>
  );
}

export { Notification };
